import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { Tooltip } from 'react-tippy'

import {
  CheckCircleIcon,
  DotsThreeCircleVerticalIcon,
  GavelIcon,
  PencilSimpleIcon,
  SparkleIcon,
  TimerIcon,
  TrashIcon,
  TruckIcon,
} from '@phosphor-icons/react'
import { useTheme } from 'styled-components'

import {
  BankTransferIcon,
  ButtonMenu,
  Chip,
  EnabledTransactionTypesPill,
  GiftCardIcon,
  IconButton,
  OriginalPaymentMethodIcon,
  Switch,
  Text,
} from '@reveni-io/reveni-ui'

import AppContext from '@/common/context/app.context'

import * as Styles from './styles'

const ExceptionItem = ({ exception, policyRules, index, onDelete, onEdit, onToggle, disabled }) => {
  const theme = useTheme()

  const { t, i18n } = useTranslation()
  const { info } = useContext(AppContext)
  const hasRestrictedRefundMethods = exception?.only_store_credit || exception?.only_gift_card
  const restrictedIconType = hasRestrictedRefundMethods ? 'slashed' : 'default'
  const linkedPolicyRule = policyRules?.find(rule => rule.id === exception?.policy_rule_id)

  return (
    <Styles.MainContainer index={index}>
      <Styles.Header>
        <Switch
          checked={!exception.disabled}
          onChange={e => onToggle(exception, e.target.checked)}
          label={<Text type="titleS">{exception.order_number}</Text>}
          disabled={disabled}
        />
        <Styles.DateAndMenuContainer>
          <Styles.Date>
            <TimerIcon size={20} />
            <Text type="bodyMRegular">
              {t('Expira')}:{' '}
              {new Intl.DateTimeFormat(i18n.language, {
                timeZone: info?.selectedStore?.timezone,
              }).format(new Date(exception.expires_at))}
            </Text>
          </Styles.Date>
          {!disabled && (
            <ButtonMenu
              menuItems={[
                {
                  label: (
                    <Styles.MenuItem>
                      <PencilSimpleIcon /> {t('Editar')}
                    </Styles.MenuItem>
                  ),
                  onClick: () => onEdit(exception),
                },
                {
                  label: (
                    <Styles.MenuItem>
                      <TrashIcon />
                      {t('Borrar')}
                    </Styles.MenuItem>
                  ),
                  onClick: () => onDelete(exception),
                },
              ]}
              renderButton={onClick => (
                <IconButton
                  variant="text"
                  onClick={onClick}
                  icon={<DotsThreeCircleVerticalIcon alt="Edit menu" size={24} weight="light" />}
                />
              )}
            />
          )}
        </Styles.DateAndMenuContainer>
      </Styles.Header>
      <Styles.ChipsContainer>
        <EnabledTransactionTypesPill
          refund={exception.enabled_for_refunds}
          exchange={exception.enabled_for_exchanges}
          isDisabled={exception.disabled}
        />
        {linkedPolicyRule && (
          <Chip
            disabled={exception.disabled}
            backgroundColor={theme.colors.disabled.surface}
            Icon={GavelIcon}
            label={`${linkedPolicyRule.internal_id} | ${linkedPolicyRule.name}`}
          />
        )}
      </Styles.ChipsContainer>
      <Styles.MethodsAndInfoContainer>
        <Styles.MethodsAndChipsContainer>
          <Tooltip
            title={
              hasRestrictedRefundMethods
                ? t('Solo habilitado Crédito en tienda')
                : t('Habilitados todos los métodos de reembolso')
            }
          >
            <Styles.MethodsContainer>
              <Styles.MethodContainer isDisabled={exception.disabled || hasRestrictedRefundMethods}>
                <BankTransferIcon size={16} type={restrictedIconType} />
              </Styles.MethodContainer>
              <Styles.MethodContainer isDisabled={exception.disabled || hasRestrictedRefundMethods}>
                <OriginalPaymentMethodIcon size={16} type={restrictedIconType} />
              </Styles.MethodContainer>
              <Styles.MethodContainer isDisabled={exception.disabled}>
                <GiftCardIcon size={16} />
              </Styles.MethodContainer>
            </Styles.MethodsContainer>
          </Tooltip>
          {exception.disable_return_fees && (
            <Chip
              disabled={exception.disabled}
              backgroundColor={theme.colors.warning.surface}
              color={theme.colors.warning.onSurface}
              label={<Text type="bodySRegular">{t('Transacción gratuita')}</Text>}
              Icon={SparkleIcon}
            />
          )}
          {exception.refund_shipping_amount && (
            <Chip
              disabled={exception.disabled}
              backgroundColor={theme.colors.warning.surface}
              color={theme.colors.warning.onSurface}
              label={<Text type="bodySRegular">{t('Gastos de envío')}</Text>}
              Icon={TruckIcon}
            />
          )}
        </Styles.MethodsAndChipsContainer>
        <div>
          {exception?.is_used && (
            <Chip
              backgroundColor={theme.colors.success.surface}
              color={theme.colors.success.onSurface}
              Icon={CheckCircleIcon}
              label={<Text type="bodySRegular">{t('Usada')}</Text>}
            />
          )}
        </div>
      </Styles.MethodsAndInfoContainer>
    </Styles.MainContainer>
  )
}

export default ExceptionItem
